import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { DocumentTypeConfig } from '../../config/documentTypes';
import { useDocumentUpload } from '../../hooks/useDocumentCollection';

interface UploadResult {
  uploadId: string;
  fileName: string;
}

interface DocumentDropzoneProps {
  documentType: DocumentTypeConfig;
  dealId: string;
  sessionId: string | null;
  formType?: 'standard' | 'retrospective';
  onUploadStart: () => void;
  onUploadComplete: (uploadId: string, newSessionId: string) => void;
  onUploadError: (error: string) => void;
  disabled?: boolean;
}

export const DocumentDropzone: React.FC<DocumentDropzoneProps> = ({
  documentType,
  dealId,
  sessionId,
  formType,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  disabled = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useDocumentUpload();

  /**
   * Validate file extension against accepted types
   */
  const validateFileType = useCallback(
    (file: File): boolean => {
      if (!documentType.accept) {
        return true; // No restriction - accept all
      }

      const acceptedExtensions = documentType.accept
        .split(',')
        .map((ext) => ext.trim().toLowerCase());

      // Handle files without extensions
      const lastDotIndex = file.name.lastIndexOf('.');
      if (lastDotIndex === -1 || lastDotIndex === 0) {
        // No extension - only valid if accept includes empty extension or wildcard
        return false;
      }

      const fileExtension = file.name.substring(lastDotIndex).toLowerCase();
      return acceptedExtensions.includes(fileExtension);
    },
    [documentType.accept]
  );

  /**
   * Validate file size against max size (in MB)
   */
  const validateFileSize = useCallback(
    (file: File): boolean => {
      const maxSizeBytes = documentType.maxSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    [documentType.maxSize]
  );

  /**
   * Process file after selection or drop
   */
  const processFile = useCallback(
    async (selectedFile: File) => {
      // Reset state
      setError(null);
      setUploadResult(null);

      // Validate file type
      if (!validateFileType(selectedFile)) {
        const errorMsg = `Invalid file type. Accepted types: ${documentType.accept || 'all'}`;
        setError(errorMsg);
        onUploadError(errorMsg);
        return;
      }

      // Validate file size
      if (!validateFileSize(selectedFile)) {
        const errorMsg = `File too large. Maximum size: ${documentType.maxSize}MB`;
        setError(errorMsg);
        onUploadError(errorMsg);
        return;
      }

      // Set file and start upload
      setFile(selectedFile);
      setUploading(true);
      onUploadStart();

      try {
        const result = await uploadFile(
          dealId,
          sessionId,
          documentType.key,
          selectedFile,
          formType
        );

        if (result) {
          setUploadResult({
            uploadId: result.uploadId,
            fileName: result.fileName,
          });
          onUploadComplete(result.uploadId, result.sessionId);
        } else {
          throw new Error('Upload failed - no response received');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMsg);
        onUploadError(errorMsg);
        setFile(null);
      } finally {
        setUploading(false);
      }
    },
    [
      dealId,
      sessionId,
      formType,
      documentType.key,
      documentType.accept,
      documentType.maxSize,
      validateFileType,
      validateFileSize,
      uploadFile,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    ]
  );

  /**
   * Handle file drop
   */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || uploading) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        processFile(droppedFiles[0]);
      }
    },
    [disabled, uploading, processFile]
  );

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !uploading) {
        setIsDragging(true);
      }
    },
    [disabled, uploading]
  );

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  /**
   * Handle click to browse
   */
  const handleClick = useCallback(() => {
    if (disabled || uploading || uploadResult) return;
    fileInputRef.current?.click();
  }, [disabled, uploading, uploadResult]);

  /**
   * Handle file input change
   */
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        processFile(selectedFiles[0]);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFile]
  );

  /**
   * Get border color based on state
   */
  const getBorderColor = (): string => {
    if (error) return 'error.main';
    if (uploadResult) return 'success.main';
    if (isDragging) return 'primary.main';
    return 'grey.400';
  };

  /**
   * Get background color based on state
   */
  const getBackgroundColor = (): string => {
    if (error) return 'error.lighter';
    if (uploadResult) return 'success.lighter';
    if (isDragging) return 'action.hover';
    return 'background.paper';
  };

  /**
   * Get accepted file types display text
   */
  const getAcceptedTypesText = (): string => {
    if (!documentType.accept) {
      return 'All file types accepted';
    }
    return `Accepted: ${documentType.accept}`;
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      sx={{
        border: '2px dashed',
        borderColor: getBorderColor(),
        borderRadius: 2,
        p: 3,
        backgroundColor: getBackgroundColor(),
        cursor: disabled || uploading || uploadResult ? 'default' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        opacity: disabled ? 0.5 : 1,
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        '&:hover': {
          borderColor:
            !disabled && !uploading && !uploadResult
              ? 'primary.main'
              : getBorderColor(),
          backgroundColor:
            !disabled && !uploading && !uploadResult
              ? 'action.hover'
              : getBackgroundColor(),
        },
      }}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={documentType.accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />

      {/* Uploading State */}
      {uploading && (
        <>
          <CircularProgress size={40} sx={{ mb: 1.5 }} />
          <Typography variant="body1" color="text.secondary">
            Uploading...
          </Typography>
          {file && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.name}
            </Typography>
          )}
        </>
      )}

      {/* Success State */}
      {!uploading && uploadResult && (
        <>
          <CheckCircle color="success" sx={{ fontSize: 40, mb: 1.5 }} />
          <Typography variant="body1" color="success.main" fontWeight={500}>
            Upload Complete
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {uploadResult.fileName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            This file will be included in your submission
          </Typography>
        </>
      )}

      {/* Error State */}
      {!uploading && !uploadResult && error && (
        <>
          <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1.5 }} />
          <Typography variant="body1" color="error.main" fontWeight={500}>
            Upload Failed
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.5 }}
          >
            Click or drag to try again
          </Typography>
        </>
      )}

      {/* Idle State */}
      {!uploading && !uploadResult && !error && (
        <>
          <CloudUpload
            sx={{
              fontSize: 40,
              mb: 1.5,
              color: isDragging ? 'primary.main' : 'text.secondary',
            }}
          />
          <Typography
            variant="body1"
            color={isDragging ? 'primary.main' : 'text.secondary'}
            fontWeight={isDragging ? 500 : 400}
          >
            {isDragging
              ? 'Drop file here'
              : 'Drag and drop or click to browse'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {getAcceptedTypesText()} (max {documentType.maxSize}MB)
          </Typography>
        </>
      )}
    </Box>
  );
};

export default DocumentDropzone;
